import React, { useMemo } from 'react';
import './ampol-cash-address-form.scss';
import { Form, InputType } from '@ampol/design-system';
import * as yup from 'yup';
import { string } from 'yup';
import { AUSSIE_STATES, addressValidationSchema } from '../../../../utilities/constants';
import Regex from 'constants/regex';
import Validation from 'constants/validation-messages';

/**
 * Shipping address form for entering a shipping address
 * @param shippingAddress Address properties to update
 * @param onSubmit Form submit event
 * @param formId Form unique id
 * @param onStateChange Form state change event
 * @param formType 'shipping' or 'digital' form
 * @returns JSX Element
 */
const AmpolCashAddressForm = ({ shippingAddress, onSubmit, formId, onStateChange, formType }) => {
  const isDigitalCartOnly = formType === 'digital';

  const {
    firstName,
    lastName,
    email,
    mobile,
    address,
    suburb,
    state,
    postcode,
    message,
  } = shippingAddress || {};
  const defaultValues = useMemo(() => (shippingAddress ? {
    firstName,
    lastName,
    email,
    mobile,
    address,
    suburb,
    state: state?.split(';')?.[0],
    postcode,
    message,
  } : {}), [firstName, lastName, email, mobile, address, suburb, state, postcode, message]);

  let validationSchema = yup.object().shape({
    firstName: string().transform((value) => value ? value.trim() : value).nullable().max(27, Validation.MAX_CHAR_TWENTY_SEVEN)
      .matches(Regex.ALPHA_NUM_SPACE_SPECIAL_CHARACTER, Validation.ALPHA_NUM_SPECIAL_CHARACTER),
    lastName: string().transform((value) => value ? value.trim() : value).nullable().max(27, Validation.MAX_CHAR_TWENTY_SEVEN)
      .matches(Regex.ALPHA_NUM_SPACE_SPECIAL_CHARACTER, Validation.ALPHA_NUM_SPECIAL_CHARACTER),
  });

  const shippingAddressSchema = yup.object().shape({
    email: string()
      .nullable()
      .email(Validation.EMAIL_VALID),
    mobile: string().nullable().matches(Regex.MOBILE, {
      excludeEmptyString: true,
      message: Validation.MOBILE,
    }),
    address: addressValidationSchema.fields.addressLine2.required(Validation.ADDRESS_REQUIRED),
    suburb: addressValidationSchema.fields.city.required(Validation.SUBURB_REQUIRED),
    state: addressValidationSchema.fields.state,
    postcode: addressValidationSchema.fields.postcode,
  });

  const digitalAddressSchema = yup.object().shape({
    email: string()
      .nullable()
      .required(Validation.EMAIL_REQUIRED)
      .email(Validation.EMAIL_VALID),
    message: string().transform((value) => value ? value.trim() : value).nullable().max(300, Validation.MAX_CHAR_THREE_HUNDRED)
      .matches(Regex.CASH_CUSTOM_MESSAGE, Validation.CUSTOM_MESSAGE),
  });

  if (isDigitalCartOnly) {
    validationSchema = validationSchema.concat(digitalAddressSchema);
  } else {
    validationSchema = validationSchema.concat(shippingAddressSchema);
  }

  return <>
    <div className="recipient-address">
      <Form
        id={formId}
        schema={validationSchema}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        onStateChange={onStateChange}
        HTMLFormProps={{ noValidate: true }}
      >
        <div className="recipient-address__fields">
          <Form.Input label={'First Name'} name={'firstName'}/>
          <Form.Input label={'Last Name'} name={'lastName'}/>
          <Form.Input label={'Email'} inputType={InputType.email} name={'email'}/>

          {!isDigitalCartOnly && <>
            <Form.Input label={'Mobile'} name={'mobile'} />
            <Form.Input label={'Street Address'} aria-label={'Street Address'} name={'address'} className={'recipient-address__field100'} />
            <Form.Input label={'Suburb'} name={'suburb'} className={'recipient-address__field100'} />
            <Form.DropdownSearch name={'state'} type={'dropdown'} placeholder={'State'} data={AUSSIE_STATES} label={'State'} />
            <Form.Input label={'Postcode'} name={'postcode'} />
          </>}

          {isDigitalCartOnly && <>
            <Form.TextArea
              label="Message"
              name="message"
              rows={5}
              className={'recipient-address__field100'}
            />
          </>}
        </div>
      </Form>
    </div>
  </>;
};

export default AmpolCashAddressForm;
